import type { Language, Locale } from '@/config/locales';

export interface CartModalTranslation {
  cart: string;
  emptyCart: string;
  emptyCartDescription: string;
  clearCart: string;
  checkout: string;
  subtotal: string;
  deliveryFee: string;
  total: string;
  free: string;
  estimatedTime: string;
  minutes: string;
  remove: string;
  quantity: string;
  discount: string;
  confirmClear: string;
  confirmClearDescription: string;
  cancel: string;
  confirm: string;
  tableItems: string;
  /** #633 테이블 세션 배지 접두사(예: "Mesa"/"Table"/"테이블"). 하드코딩 'Mesa' 제거. */
  tableLabel: string;
  confirmDelete: string;
  confirmDeleteDescription: string;
  delete: string;
  change: string;
  default: string;
  differentStoreConfirm: string;
}

// #512 — Locale 단일 출처 정합.
export type CartModalTranslations =
  & Record<Language, CartModalTranslation>
  & Record<Exclude<Locale, Language>, CartModalTranslation>;

export const cartTranslations: CartModalTranslations = {
  ko: {
    cart: '장바구니',
    emptyCart: '장바구니가 비어있습니다',
    emptyCartDescription: '메뉴를 선택하여 장바구니에 추가해보세요',
    clearCart: '장바구니 비우기',
    checkout: '주문하기',
    subtotal: '소계',
    deliveryFee: '배달비',
    total: '총합',
    free: '무료',
    estimatedTime: '예상 시간',
    minutes: '분',
    remove: '제거',
    quantity: '수량',
    discount: '할인',
    confirmClear: '장바구니를 비우시겠습니까?',
    confirmClearDescription: '모든 아이템이 삭제됩니다.',
    cancel: '취소',
    confirm: '확인',
    tableItems: '테이블 항목',
    tableLabel: '테이블',
    confirmDelete: '삭제하시겠습니까?',
    confirmDeleteDescription: '이 항목이 장바구니에서 삭제됩니다.',
    delete: '삭제',
    change: '변경',
    default: '기본',
    differentStoreConfirm: '다른 매장의 메뉴입니다. 기존 장바구니를 비우고 추가하시겠습니까?',
  },
  es: {
    cart: 'Carrito',
    emptyCart: 'El carrito está vacío',
    emptyCartDescription: 'Selecciona elementos del menú para agregar al carrito',
    clearCart: 'Vaciar carrito',
    checkout: 'Realizar pedido',
    subtotal: 'Subtotal',
    deliveryFee: 'Costo de entrega',
    total: 'Total',
    free: 'Gratis',
    estimatedTime: 'Tiempo estimado',
    minutes: 'min',
    remove: 'Eliminar',
    quantity: 'Cantidad',
    discount: 'Descuento',
    confirmClear: '¿Vaciar el carrito?',
    confirmClearDescription: 'Se eliminarán todos los elementos.',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    tableItems: 'Artículos de mesa',
    tableLabel: 'Mesa',
    confirmDelete: '¿Eliminar este artículo?',
    confirmDeleteDescription: 'Este artículo se eliminará del carrito.',
    delete: 'Eliminar',
    change: 'Cambiar',
    default: 'Predeterminado',
    differentStoreConfirm: 'Este artículo es de otra tienda. ¿Vaciar el carrito y agregar?',
  },
  en: {
    cart: 'Cart',
    emptyCart: 'Your cart is empty',
    emptyCartDescription: 'Select items from the menu to add to your cart',
    clearCart: 'Clear cart',
    checkout: 'Checkout',
    subtotal: 'Subtotal',
    deliveryFee: 'Delivery fee',
    total: 'Total',
    free: 'Free',
    estimatedTime: 'Estimated time',
    minutes: 'min',
    remove: 'Remove',
    quantity: 'Quantity',
    discount: 'Discount',
    confirmClear: 'Clear cart?',
    confirmClearDescription: 'All items will be removed.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    tableItems: 'Table items',
    tableLabel: 'Table',
    confirmDelete: 'Delete this item?',
    confirmDeleteDescription: 'This item will be removed from your cart.',
    delete: 'Delete',
    change: 'Change',
    default: 'Default',
    differentStoreConfirm: 'This item is from a different store. Clear cart and add?',
  },
  pt: {
    cart: 'Carrinho',
    emptyCart: 'Seu carrinho está vazio',
    emptyCartDescription: 'Selecione itens do menu para adicionar ao carrinho',
    clearCart: 'Esvaziar carrinho',
    checkout: 'Finalizar pedido',
    subtotal: 'Subtotal',
    deliveryFee: 'Taxa de entrega',
    total: 'Total',
    free: 'Grátis',
    estimatedTime: 'Tempo estimado',
    minutes: 'min',
    remove: 'Remover',
    quantity: 'Quantidade',
    discount: 'Desconto',
    confirmClear: 'Esvaziar o carrinho?',
    confirmClearDescription: 'Todos os itens serão removidos.',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    tableItems: 'Itens da mesa',
    tableLabel: 'Mesa',
    confirmDelete: 'Excluir este item?',
    confirmDeleteDescription: 'Este item será removido do carrinho.',
    delete: 'Excluir',
    change: 'Alterar',
    default: 'Padrão',
    differentStoreConfirm: 'Este item é de outra loja. Esvaziar o carrinho e adicionar?',
  },
  fr: {
    cart: 'Panier',
    emptyCart: 'Votre panier est vide',
    emptyCartDescription: 'Sélectionnez des articles du menu à ajouter au panier',
    clearCart: 'Vider le panier',
    checkout: 'Passer commande',
    subtotal: 'Sous-total',
    deliveryFee: 'Frais de livraison',
    total: 'Total',
    free: 'Gratuit',
    estimatedTime: 'Temps estimé',
    minutes: 'min',
    remove: 'Retirer',
    quantity: 'Quantité',
    discount: 'Remise',
    confirmClear: 'Vider le panier ?',
    confirmClearDescription: 'Tous les articles seront supprimés.',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    tableItems: 'Articles de la table',
    tableLabel: 'Table',
    confirmDelete: 'Supprimer cet article ?',
    confirmDeleteDescription: 'Cet article sera retiré du panier.',
    delete: 'Supprimer',
    change: 'Modifier',
    default: 'Par défaut',
    differentStoreConfirm: 'Cet article provient d’une autre boutique. Vider le panier et ajouter ?',
  },
  zh: {
    cart: '购物车',
    emptyCart: '购物车是空的',
    emptyCartDescription: '从菜单中选择项目添加到购物车',
    clearCart: '清空购物车',
    checkout: '结账',
    subtotal: '小计',
    deliveryFee: '配送费',
    total: '总计',
    free: '免费',
    estimatedTime: '预计时间',
    minutes: '分钟',
    remove: '移除',
    quantity: '数量',
    discount: '折扣',
    confirmClear: '清空购物车?',
    confirmClearDescription: '所有项目将被移除。',
    cancel: '取消',
    confirm: '确认',
    tableItems: '桌面项目',
    tableLabel: '餐桌',
    confirmDelete: '删除此项目?',
    confirmDeleteDescription: '此项目将从购物车中移除。',
    delete: '删除',
    change: '更改',
    default: '默认',
    differentStoreConfirm: '此项目来自其他门店。清空购物车并添加?',
  },
  ja: {
    cart: 'カート',
    emptyCart: 'カートは空です',
    emptyCartDescription: 'メニューから項目を選択してカートに追加してください',
    clearCart: 'カートを空にする',
    checkout: '注文する',
    subtotal: '小計',
    deliveryFee: '配送料',
    total: '合計',
    free: '無料',
    estimatedTime: '予想時間',
    minutes: '分',
    remove: '削除',
    quantity: '数量',
    discount: '割引',
    confirmClear: 'カートを空にしますか？',
    confirmClearDescription: 'すべての項目が削除されます。',
    cancel: 'キャンセル',
    confirm: '確認',
    tableItems: 'テーブル項目',
    tableLabel: 'テーブル',
    confirmDelete: 'この項目を削除しますか？',
    confirmDeleteDescription: 'この項目はカートから削除されます。',
    delete: '削除',
    change: '変更',
    default: 'デフォルト',
    differentStoreConfirm: 'この項目は別の店舗のものです。カートを空にして追加しますか？',
  },
  hi: {
    cart: 'कार्ट',
    emptyCart: 'आपका कार्ट खाली है',
    emptyCartDescription: 'कार्ट में जोड़ने के लिए मेनू से आइटम चुनें',
    clearCart: 'कार्ट खाली करें',
    checkout: 'चेकआउट',
    subtotal: 'उप-योग',
    deliveryFee: 'डिलीवरी शुल्क',
    total: 'कुल',
    free: 'मुफ्त',
    estimatedTime: 'अनुमानित समय',
    minutes: 'मिनट',
    remove: 'हटाएं',
    quantity: 'मात्रा',
    discount: 'छूट',
    confirmClear: 'कार्ट खाली करें?',
    confirmClearDescription: 'सभी आइटम हटा दिए जाएंगे।',
    cancel: 'रद्द करें',
    confirm: 'पुष्टि करें',
    tableItems: 'टेबल आइटम',
    tableLabel: 'टेबल',
    confirmDelete: 'इस आइटम को हटाएं?',
    confirmDeleteDescription: 'यह आइटम आपके कार्ट से हटा दिया जाएगा।',
    delete: 'हटाएं',
    change: 'बदलें',
    default: 'डिफ़ॉल्ट',
    differentStoreConfirm: 'यह आइटम किसी अन्य स्टोर का है। कार्ट खाली करें और जोड़ें?',
  },
  de: {
    cart: 'Warenkorb',
    emptyCart: 'Ihr Warenkorb ist leer',
    emptyCartDescription: 'Wählen Sie Artikel aus dem Menü, um sie dem Warenkorb hinzuzufügen',
    clearCart: 'Warenkorb leeren',
    checkout: 'Zur Kasse',
    subtotal: 'Zwischensumme',
    deliveryFee: 'Liefergebühr',
    total: 'Gesamt',
    free: 'Kostenlos',
    estimatedTime: 'Geschätzte Zeit',
    minutes: 'Min.',
    remove: 'Entfernen',
    quantity: 'Menge',
    discount: 'Rabatt',
    confirmClear: 'Warenkorb leeren?',
    confirmClearDescription: 'Alle Artikel werden entfernt.',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    tableItems: 'Tischartikel',
    tableLabel: 'Tisch',
    confirmDelete: 'Diesen Artikel löschen?',
    confirmDeleteDescription: 'Dieser Artikel wird aus Ihrem Warenkorb entfernt.',
    delete: 'Löschen',
    change: 'Ändern',
    default: 'Standard',
    differentStoreConfirm: 'Dieser Artikel ist von einem anderen Geschäft. Warenkorb leeren und hinzufügen?',
  },
  it: {
    cart: 'Carrello',
    emptyCart: 'Il tuo carrello è vuoto',
    emptyCartDescription: 'Seleziona elementi dal menu da aggiungere al carrello',
    clearCart: 'Svuota carrello',
    checkout: 'Procedi al pagamento',
    subtotal: 'Subtotale',
    deliveryFee: 'Costo di consegna',
    total: 'Totale',
    free: 'Gratis',
    estimatedTime: 'Tempo stimato',
    minutes: 'min',
    remove: 'Rimuovi',
    quantity: 'Quantità',
    discount: 'Sconto',
    confirmClear: 'Svuotare il carrello?',
    confirmClearDescription: 'Tutti gli elementi verranno rimossi.',
    cancel: 'Annulla',
    confirm: 'Conferma',
    tableItems: 'Articoli del tavolo',
    tableLabel: 'Tavolo',
    confirmDelete: 'Eliminare questo articolo?',
    confirmDeleteDescription: 'Questo articolo verrà rimosso dal carrello.',
    delete: 'Elimina',
    change: 'Cambia',
    default: 'Predefinito',
    differentStoreConfirm: 'Questo articolo è di un altro negozio. Svuotare il carrello e aggiungere?',
  },
  ru: {
    cart: 'Корзина',
    emptyCart: 'Ваша корзина пуста',
    emptyCartDescription: 'Выберите элементы из меню, чтобы добавить в корзину',
    clearCart: 'Очистить корзину',
    checkout: 'Оформить заказ',
    subtotal: 'Промежуточный итог',
    deliveryFee: 'Стоимость доставки',
    total: 'Итого',
    free: 'Бесплатно',
    estimatedTime: 'Ожидаемое время',
    minutes: 'мин',
    remove: 'Удалить',
    quantity: 'Количество',
    discount: 'Скидка',
    confirmClear: 'Очистить корзину?',
    confirmClearDescription: 'Все элементы будут удалены.',
    cancel: 'Отмена',
    confirm: 'Подтвердить',
    tableItems: 'Элементы стола',
    tableLabel: 'Стол',
    confirmDelete: 'Удалить этот элемент?',
    confirmDeleteDescription: 'Этот элемент будет удалён из корзины.',
    delete: 'Удалить',
    change: 'Изменить',
    default: 'По умолчанию',
    differentStoreConfirm: 'Этот элемент из другого магазина. Очистить корзину и добавить?',
  },
  ar: {
    cart: 'السلة',
    emptyCart: 'سلتك فارغة',
    emptyCartDescription: 'اختر عناصر من القائمة لإضافتها إلى السلة',
    clearCart: 'إفراغ السلة',
    checkout: 'الدفع',
    subtotal: 'المجموع الفرعي',
    deliveryFee: 'رسوم التوصيل',
    total: 'الإجمالي',
    free: 'مجاني',
    estimatedTime: 'الوقت المقدر',
    minutes: 'دقيقة',
    remove: 'إزالة',
    quantity: 'الكمية',
    discount: 'خصم',
    confirmClear: 'إفراغ السلة؟',
    confirmClearDescription: 'سيتم إزالة جميع العناصر.',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    tableItems: 'عناصر الطاولة',
    tableLabel: 'طاولة',
    confirmDelete: 'حذف هذا العنصر؟',
    confirmDeleteDescription: 'سيتم إزالة هذا العنصر من سلتك.',
    delete: 'حذف',
    change: 'تغيير',
    default: 'افتراضي',
    differentStoreConfirm: 'هذا العنصر من متجر مختلف. إفراغ السلة وإضافة؟',
  },
  vi: {
    cart: 'Giỏ hàng',
    emptyCart: 'Giỏ hàng của bạn trống',
    emptyCartDescription: 'Chọn các mục từ menu để thêm vào giỏ hàng',
    clearCart: 'Làm trống giỏ hàng',
    checkout: 'Thanh toán',
    subtotal: 'Tạm tính',
    deliveryFee: 'Phí vận chuyển',
    total: 'Tổng cộng',
    free: 'Miễn phí',
    estimatedTime: 'Thời gian ước tính',
    minutes: 'phút',
    remove: 'Xóa',
    quantity: 'Số lượng',
    discount: 'Giảm giá',
    confirmClear: 'Làm trống giỏ hàng?',
    confirmClearDescription: 'Tất cả các mục sẽ bị xóa.',
    cancel: 'Hủy',
    confirm: 'Xác nhận',
    tableItems: 'Mục bàn',
    tableLabel: 'Bàn',
    confirmDelete: 'Xóa mục này?',
    confirmDeleteDescription: 'Mục này sẽ bị xóa khỏi giỏ hàng.',
    delete: 'Xóa',
    change: 'Thay đổi',
    default: 'Mặc định',
    differentStoreConfirm: 'Mục này từ cửa hàng khác. Làm trống giỏ hàng và thêm?',
  },
  th: {
    cart: 'ตะกร้า',
    emptyCart: 'ตะกร้าของคุณว่างเปล่า',
    emptyCartDescription: 'เลือกรายการจากเมนูเพื่อเพิ่มลงในตะกร้า',
    clearCart: 'ล้างตะกร้า',
    checkout: 'ชำระเงิน',
    subtotal: 'ยอดรวมย่อย',
    deliveryFee: 'ค่าจัดส่ง',
    total: 'ยอดรวม',
    free: 'ฟรี',
    estimatedTime: 'เวลาประมาณ',
    minutes: 'นาที',
    remove: 'ลบ',
    quantity: 'จำนวน',
    discount: 'ส่วนลด',
    confirmClear: 'ล้างตะกร้า?',
    confirmClearDescription: 'รายการทั้งหมดจะถูกลบ',
    cancel: 'ยกเลิก',
    confirm: 'ยืนยัน',
    tableItems: 'รายการโต๊ะ',
    tableLabel: 'โต๊ะ',
    confirmDelete: 'ลบรายการนี้?',
    confirmDeleteDescription: 'รายการนี้จะถูกลบออกจากตะกร้าของคุณ',
    delete: 'ลบ',
    change: 'เปลี่ยน',
    default: 'ค่าเริ่มต้น',
    differentStoreConfirm: 'รายการนี้มาจากร้านอื่น ล้างตะกร้าและเพิ่ม?',
  },
  nl: {
    cart: 'Winkelwagen',
    emptyCart: 'Uw winkelwagen is leeg',
    emptyCartDescription: 'Selecteer items uit het menu om aan uw winkelwagen toe te voegen',
    clearCart: 'Winkelwagen legen',
    checkout: 'Afrekenen',
    subtotal: 'Subtotaal',
    deliveryFee: 'Bezorgkosten',
    total: 'Totaal',
    free: 'Gratis',
    estimatedTime: 'Geschatte tijd',
    minutes: 'min',
    remove: 'Verwijderen',
    quantity: 'Aantal',
    discount: 'Korting',
    confirmClear: 'Winkelwagen legen?',
    confirmClearDescription: 'Alle items worden verwijderd.',
    cancel: 'Annuleren',
    confirm: 'Bevestigen',
    tableItems: 'Tafelartikelen',
    tableLabel: 'Tafel',
    confirmDelete: 'Dit item verwijderen?',
    confirmDeleteDescription: 'Dit item wordt uit uw winkelwagen verwijderd.',
    delete: 'Verwijderen',
    change: 'Wijzigen',
    default: 'Standaard',
    differentStoreConfirm: 'Dit item is van een andere winkel. Winkelwagen legen en toevoegen?',
  },
};
